import React, { useState, useEffect } from 'react';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ChevronLeft, ChevronRight, Clock, MapPin, Plus, AlertCircle, CheckCircle, CheckSquare, Timer, Zap, Calendar as CalendarIcon, CalendarCheck, CalendarX, Pause, AlertTriangle, Flag, Flame, XCircle, Trash2 } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, startOfWeek, endOfWeek, isSameMonth, isSameDay, isToday } from 'date-fns';
import { Event, Task } from '@/types/scheduler';
import { cn } from '@/lib/utils';
import { 
  formatInLocalTime, 
  isTodayInLocal, 
  isSameDayInLocal, 
  nowInLocal,
  fromApiFormat
} from '@/utils/timezone';
import { 
  sortEventsByTime, 
  sortTasksByTime, 
  EventWithStatus, 
  TaskWithStatus 
} from '@/utils/eventStatus';

interface CalendarViewProps {
  events: Event[];
  tasks: Task[];
  onEventClick?: (event: Event) => void;
  onTaskClick?: (task: Task) => void;
  onDateClick?: (date: Date) => void;
  onCompleteEvent?: (eventId: string, title: string) => void;
  onIncompleteEvent?: (eventId: string, title: string) => void;
  onDeleteEvent?: (eventId: string, title: string) => void;
}

export const CalendarView: React.FC<CalendarViewProps> = ({
  events,
  tasks,
  onEventClick,
  onTaskClick,
  onDateClick,
  onCompleteEvent,
  onIncompleteEvent,
  onDeleteEvent,
}) => {
  const [currentMonth, setCurrentMonth] = useState(nowInLocal());
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(nowInLocal());

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);
  const daysInMonth = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const getEventsForDate = (date: Date) => {
    return events.filter(event =>
      isSameDayInLocal(event.start_datetime, date)
    );
  };

  const getTasksForDate = (date: Date) => {
    return tasks.filter(task =>
      task.due_date && isSameDayInLocal(task.due_date, date)
    );
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-500 hover:bg-red-600';
      case 'high': return 'bg-orange-500 hover:bg-orange-600';
      case 'medium': return 'bg-yellow-500 hover:bg-yellow-600';
      case 'low': return 'bg-green-500 hover:bg-green-600';
      default: return 'bg-gray-500 hover:bg-gray-600';
    }
  };

  const handlePreviousMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1));
  };

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    if (date && onDateClick) {
      onDateClick(date);
    }
  };

  const selectedDateEvents = selectedDate ? getEventsForDate(selectedDate) : [];
  const selectedDateTasks = selectedDate ? getTasksForDate(selectedDate) : [];
  
  // Sort events and tasks by time status
  const sortedEvents = sortEventsByTime(selectedDateEvents);
  const sortedTasks = sortTasksByTime(selectedDateTasks);
  
  const getTimeStatusIcon = (status: string) => {
    switch (status) {
      case 'current': return <Timer className="h-4 w-4 text-green-600 dark:text-green-400 animate-pulse" />;
      case 'missed': return <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />;
      case 'next': return <Zap className="h-4 w-4 text-blue-600 dark:text-blue-400" />;
      case 'overdue': return <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />;
      case 'due-today': return <Clock className="h-4 w-4 text-orange-600 dark:text-orange-400" />;
      default: return <Clock className="h-4 w-4 text-muted-foreground" />;
    }
  };
  
  const getTimeStatusColor = (status: string) => {
    switch (status) {
      case 'current': return 'border-l-4 border-green-500 bg-green-50 dark:bg-green-950/30 dark:border-green-400';
      case 'missed': return 'border-l-4 border-red-500 bg-red-50 dark:bg-red-950/30 dark:border-red-400';
      case 'next': return 'border-l-4 border-blue-500 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-400 ring-2 ring-blue-200 dark:ring-blue-800';
      case 'overdue': return 'border-l-4 border-red-500 bg-red-50 dark:bg-red-950/30 dark:border-red-400';
      case 'due-today': return 'border-l-4 border-orange-500 bg-orange-50 dark:bg-orange-950/30 dark:border-orange-400';
      case 'completed': return 'border-l-4 border-muted bg-muted/30 dark:bg-muted/20 opacity-60';
      default: return 'border-l-4 border-gray-200 dark:border-gray-700';
    }
  };
  
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'scheduled': return <CalendarCheck className="h-4 w-4 text-green-600 dark:text-green-400" />;
      case 'pending': return <Clock className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />;
      case 'cancelled': return <CalendarX className="h-4 w-4 text-red-600 dark:text-red-400" />;
      case 'completed': return <CheckSquare className="h-4 w-4 text-green-600 dark:text-green-400" />;
      case 'postponed': return <Pause className="h-4 w-4 text-orange-600 dark:text-orange-400" />;
      case 'todo': return <CalendarIcon className="h-4 w-4 text-gray-600 dark:text-gray-400" />;
      case 'in_progress': return <Timer className="h-4 w-4 text-blue-600 dark:text-blue-400" />;
      default: return <CalendarIcon className="h-4 w-4 text-gray-600 dark:text-gray-400" />;
    }
  };
  
  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'urgent': return <Flame className="h-4 w-4 text-red-600 dark:text-red-400" />;
      case 'high': return <AlertTriangle className="h-4 w-4 text-orange-600 dark:text-orange-400" />;
      case 'medium': return <Flag className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />;
      case 'low': return <Flag className="h-4 w-4 text-green-600 dark:text-green-400" />;
      default: return <Flag className="h-4 w-4 text-gray-600 dark:text-gray-400" />;
    }
  };
  
  const getStatusTooltip = (status: string) => {
    switch (status) {
      case 'scheduled': return 'Event is confirmed and scheduled';
      case 'pending': return 'Event is tentative, awaiting confirmation';
      case 'cancelled': return 'Event has been cancelled';
      case 'completed': return 'Event has been completed';
      case 'postponed': return 'Event has been postponed to a later time';
      case 'todo': return 'Task is pending completion';
      case 'in_progress': return 'Task is currently being worked on';
      default: return 'Status unknown';
    }
  };
  
  const getPriorityTooltip = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'Urgent - Requires immediate attention';
      case 'high': return 'High priority - Important and time-sensitive';
      case 'medium': return 'Medium priority - Standard importance';
      case 'low': return 'Low priority - Can be done when time permits';
      default: return 'Priority not set';
    }
  };

  // Auto-select today's date on mount but don't trigger onDateClick
  useEffect(() => {
    const today = nowInLocal();
    setSelectedDate(today);
  }, []);

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      {/* Calendar Grid */}
      <div className="lg:col-span-2">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <CardTitle className="text-lg font-semibold">
              {format(currentMonth, 'MMMM yyyy')}
            </CardTitle>
            <div className="flex gap-1">
              <Button variant="outline" size="icon" onClick={handlePreviousMonth}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="icon" onClick={handleNextMonth}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid grid-cols-7 gap-1 mb-4">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <div key={day} className="p-2 text-center text-sm font-medium text-muted-foreground">
                  {day}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {daysInMonth.map((date) => {
                const dayEvents = getEventsForDate(date);
                const dayTasks = getTasksForDate(date);
                const isSelected = selectedDate && isSameDay(date, selectedDate);
                const isCurrentDay = isTodayInLocal(date.toISOString());
                const isCurrentMonth = isSameMonth(date, currentMonth);
                
                return (
                  <div
                    key={date.toISOString()}
                    className={cn(
                      "min-h-[80px] p-1 border rounded cursor-pointer hover:bg-accent/50 transition-colors group relative",
                      isSelected && "bg-primary/10 border-primary",
                      isCurrentDay && !isSelected && "bg-accent border-accent-foreground/20",
                      isCurrentDay && isSelected && "bg-primary/20 border-primary ring-2 ring-primary/50",
                      !isCurrentMonth && "text-muted-foreground bg-muted/20"
                    )}
                    onClick={() => handleDateSelect(date)}
                  >
                    <div className="flex justify-between items-center mb-1">
                      <div className="font-medium text-sm">
                        {format(date, 'd')}
                      </div>
                      {dayEvents.length === 0 && dayTasks.length === 0 && (
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                          <Plus className="h-3 w-3 text-muted-foreground" />
                        </div>
                      )}
                    </div>
                    <div className="space-y-1">
                      {dayEvents.slice(0, 2).map((event) => (
                        <div
                          key={event.id}
                          className={cn(
                            "text-xs px-1 py-0.5 rounded text-white truncate cursor-pointer",
                            getPriorityColor(event.priority)
                          )}
                          onClick={(e) => {
                            e.stopPropagation();
                            onEventClick?.(event);
                          }}
                          title={event.title}
                        >
                          {event.title}
                        </div>
                      ))}
                      {dayTasks.slice(0, 1).map((task) => (
                        <div
                          key={task.id}
                          className="text-xs px-1 py-0.5 rounded bg-blue-100 text-blue-800 truncate cursor-pointer border border-blue-200"
                          onClick={(e) => {
                            e.stopPropagation();
                            onTaskClick?.(task);
                          }}
                          title={`Task: ${task.title}`}
                        >
                          📋 {task.title}
                        </div>
                      ))}
                      {(dayEvents.length > 2 || dayTasks.length > 1) && (
                        <div className="text-xs text-muted-foreground">
                          +{(dayEvents.length > 2 ? dayEvents.length - 2 : 0) + 
                            (dayTasks.length > 1 ? dayTasks.length - 1 : 0)} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Selected Date Details */}
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {selectedDate ? format(selectedDate, 'MMMM d, yyyy') : 'Select a Date'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {selectedDate ? (
              <>
                {/* Events for selected date */}
                {sortedEvents.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Events ({sortedEvents.length})
                    </h4>
                    <div className="space-y-2">
                      {sortedEvents.map((event) => (
                         <div
                           key={event.id}
                             className={cn(
                               "p-4 border rounded-xl hover:bg-accent/30 transition-all duration-200 shadow-sm hover:shadow-md",
                               event.status === 'completed'
                                 ? "border-l-4 border-border bg-muted/30 dark:bg-muted/20 opacity-60 hover:bg-muted/30"
                                 : getTimeStatusColor(event.timeStatus),
                               event.isNext && event.status !== 'completed' && "ring-2 ring-primary/20 shadow-lg"
                             )}
                         >
                           <div 
                             className="cursor-pointer"
                             onClick={() => onEventClick?.(event)}
                           >
                             <div className="flex items-start justify-between gap-4">
                               <div className="flex-1 min-w-0">
                                 <div className="flex items-center gap-2 mb-2">
                                   {getTimeStatusIcon(event.timeStatus)}
                                   <h5 className="text-sm font-semibold text-foreground truncate">{event.title}</h5>
                                   {event.isNext && (
                                     <Badge variant="secondary" className="text-xs px-2 py-0.5 bg-primary/10 text-primary font-medium rounded-full">
                                       NEXT
                                     </Badge>
                                   )}
                                 </div>
                                 
                                 <div className="flex items-center gap-3 text-sm text-primary font-medium mb-2">
                                   <span>
                                     {formatInLocalTime(event.start_datetime, 'h:mm a')}
                                     {event.end_datetime && 
                                       ` - ${formatInLocalTime(event.end_datetime, 'h:mm a')}`
                                     }
                                   </span>
                                   <span className="text-xs font-semibold text-muted-foreground px-2 py-1 bg-muted/50 rounded-md">
                                     {event.timeDescription}
                                   </span>
                                 </div>
                                 
                                 {event.description && (
                                   <div className="text-sm text-muted-foreground leading-relaxed mb-1">
                                     {(() => {
                                       // Remove priority text from description start
                                       let cleanDescription = event.description;
                                       const priorityRegex = /^(urgent|high|medium|low)\s+priority\s*/i;
                                       cleanDescription = cleanDescription.replace(priorityRegex, '');
                                       
                                       return cleanDescription.length > 60 
                                         ? `${cleanDescription.substring(0, 60)}...` 
                                         : cleanDescription;
                                     })()}
                                   </div>
                                 )}
                                 
                                 {event.location && (
                                   <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-2">
                                     <MapPin className="h-3.5 w-3.5" />
                                     <span className="truncate">{event.location}</span>
                                   </div>
                                 )}
                               </div>
                               
                                 <div className="flex flex-col items-center gap-1">
                                   <div className="flex items-center gap-1" title={getStatusTooltip(event.status)}>
                                     {getStatusIcon(event.status)}
                                   </div>
                                   <div className="flex items-center gap-1" title={getPriorityTooltip(event.priority)}>
                                     {getPriorityIcon(event.priority)}
                                   </div>
                                   
                                   {event.status !== 'completed' && (
                                     <TooltipProvider>
                                       <Tooltip>
                                         <TooltipTrigger asChild>
                                           <Button 
                                             variant="ghost" 
                                             size="icon"
                                             onClick={(e) => {
                                               e.stopPropagation();
                                               onCompleteEvent?.(String(event.id), event.title);
                                            }}
                                            className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-950 rounded-lg"
                                          >
                                            <CheckCircle className="h-4 w-4" />
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <p>Mark as Complete</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  )}
                                  
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button 
                                          variant="ghost" 
                                          size="icon"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            onDeleteEvent?.(String(event.id), event.title);
                                          }}
                                          className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950 rounded-lg"
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>Delete Event</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                </div>
                             </div>
                           </div>
                         </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Tasks for selected date */}
                {sortedTasks.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      📋 Tasks ({sortedTasks.length})
                    </h4>
                    <div className="space-y-2">
                      {sortedTasks.map((task) => (
                        <div
                          key={task.id}
                          className={cn(
                            "p-3 border rounded-lg hover:bg-accent/50 transition-colors cursor-pointer",
                            task.status === 'completed'
                              ? "border-l-4 border-border bg-muted/30 dark:bg-muted/20 opacity-60 hover:bg-muted/30"
                              : getTimeStatusColor(task.timeStatus)
                          )}
                          onClick={() => onTaskClick?.(task)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                {getTimeStatusIcon(task.timeStatus)}
                                <h5 className="text-sm font-medium truncate">{task.title}</h5>
                              </div>
                              {task.timeDescription && (
                                <p className="text-xs font-medium text-foreground mb-1">
                                  {task.timeDescription}
                                </p>
                              )}
                              {task.description && (
                                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                  {task.description}
                                </p>
                              )}
                               {task.due_date && (
                                  <p className="text-xs text-muted-foreground mt-1">
                                    Due: {formatInLocalTime(task.due_date, 'MMM d, h:mm a')}
                                  </p>
                               )}
                            </div>
                             <div className="flex flex-col items-center gap-1 ml-2 flex-shrink-0">
                                <div className="flex items-center gap-1" title={getStatusTooltip(task.status)}>
                                  {getStatusIcon(task.status)}
                                </div>
                                <div className="flex items-center gap-1" title={getPriorityTooltip(task.priority)}>
                                  {getPriorityIcon(task.priority)}
                                </div>
                             </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {sortedEvents.length === 0 && sortedTasks.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>No events or tasks for this date</p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2"
                      onClick={() => onDateClick?.(selectedDate)}
                    >
                      Add Event
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <p>Click on a date to see events and tasks</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};